import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Plus, Edit2, Trash2, ChevronLeft, Loader2, X, Save, Eye, EyeOff, FolderOpen, ImageIcon } from 'lucide-react';
import { COLLECTION_LABELS, COLLECTION_SCHEMAS } from '../collectionSchemas';
import RepeaterField from '../RepeaterField';
import IconPickerInput from '../components/IconPickerInput';
import MediaPickerModal from '../components/MediaPickerModal';
import api from '../api';

function ImagePreview({ src }) {
  const [errored, setErrored] = useState(false);

  useEffect(() => {
    setErrored(false);
  }, [src]);

  if (!src || errored) return null;
  return (
    <img
      key={src}
      src={src}
      alt="Preview"
      className="mt-2 h-20 w-full rounded-lg object-cover border border-slate-200 bg-slate-50"
      onError={() => setErrored(true)}
    />
  );
}

function parseJsonField(raw) {
  if (raw == null) return [];
  if (Array.isArray(raw)) return raw;
  if (typeof raw === 'string') {
    try { return JSON.parse(raw) || []; } catch { return []; }
  }
  return [];
}

function ItemForm({ schema, initial, onSave, onClose, saving }) {
  const [mediaPickerField, setMediaPickerField] = useState(null);
  const [form, setForm] = useState(() => {
    const base = {};
    schema.forEach((f) => {
      const raw = initial?.[f.key];
      if (f.type === 'json') {
        if (f.subfields) {
          base[f.key] = parseJsonField(raw);
        } else {
          base[f.key] = raw == null ? '' : (typeof raw === 'string' ? raw : JSON.stringify(raw, null, 2));
        }
      } else {
        base[f.key] = raw ?? (f.type === 'toggle' ? true : f.type === 'number' ? '' : '');
      }
    });
    return base;
  });
  const [jsonErrors, setJsonErrors] = useState({});

  const set = (key, val) => setForm((p) => ({ ...p, [key]: val }));

  const handleSave = () => {
    const errors = {};
    const out = { ...form };

    schema.forEach((f) => {
      if (f.type !== 'json') return;

      if (f.subfields) {
        const items = Array.isArray(out[f.key]) ? out[f.key] : [];
        const requiredKeys = f.subfields.filter((sf) => sf.required).map((sf) => sf.key);
        if (requiredKeys.length > 0) {
          const badIndex = items.findIndex((item) =>
            requiredKeys.some((k) => !item[k] || !String(item[k]).trim())
          );
          if (badIndex !== -1) {
            const missingLabels = requiredKeys
              .filter((k) => !items[badIndex][k] || !String(items[badIndex][k]).trim())
              .map((k) => f.subfields.find((sf) => sf.key === k)?.label || k)
              .join(', ');
            errors[f.key] = `Item ${badIndex + 1} is missing: ${missingLabels}`;
          }
        }
        out[f.key] = items.length > 0 ? items : null;
      } else {
        const v = (out[f.key] ?? '').trim();
        if (!v) {
          out[f.key] = null;
          return;
        }
        try {
          out[f.key] = JSON.parse(v);
        } catch (e) {
          errors[f.key] = e.message;
        }
      }
    });

    if (Object.keys(errors).length) {
      setJsonErrors(errors);
      return;
    }
    setJsonErrors({});
    onSave(out);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-[min(32rem,calc(100vw-2rem))] max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
          <h2 className="font-bold text-[#1F2D45]">{initial ? 'Edit Item' : 'Add Item'}</h2>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-slate-100 text-slate-500">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          {schema.map((field) => (
            <div key={field.key}>
              <label className="block text-xs font-semibold text-slate-600 mb-1.5">
                {field.label}{field.required && <span className="text-red-500 ml-0.5">*</span>}
              </label>
              {field.type === 'toggle' ? (
                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    onClick={() => set(field.key, !form[field.key])}
                    data-testid={`field-toggle-${field.key}`}
                    className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-0 p-0 transition-colors duration-200 ease-in-out focus:outline-none focus-visible:ring-2 focus-visible:ring-[#C9962E]/60 focus-visible:ring-offset-2 ${form[field.key] ? 'bg-emerald-500' : 'bg-slate-300'}`}
                  >
                    <span
                      className={`pointer-events-none inline-block h-4 w-4 rounded-full bg-white shadow-lg ring-0 transition-transform duration-200 ease-in-out ${form[field.key] ? 'translate-x-6' : 'translate-x-1'}`}
                      style={{ marginTop: '4px' }}
                    />
                  </button>
                  <span className="text-sm text-slate-600 select-none">{form[field.key] ? 'Visible' : 'Hidden'}</span>
                </div>
              ) : field.type === 'textarea' ? (
                <textarea
                  value={form[field.key]}
                  onChange={(e) => set(field.key, e.target.value)}
                  rows={field.rows || 3}
                  data-testid={`field-${field.key}`}
                  className="w-full px-3 py-2 rounded-lg border border-slate-300 text-sm focus:outline-none focus:ring-2 focus:ring-[#C9962E]/40 resize-y"
                />
              ) : field.type === 'json' ? (
                <>
                  {field.subfields ? (
                    <RepeaterField
                      value={form[field.key]}
                      onChange={(val) => set(field.key, val)}
                      subfields={field.subfields}
                      fieldLabel={field.label}
                    />
                  ) : (
                    <textarea
                      value={form[field.key]}
                      onChange={(e) => set(field.key, e.target.value)}
                      rows={6}
                      data-testid={`field-${field.key}`}
                      placeholder='e.g. [{"title": "...", "desc": "..."}]'
                      className="w-full px-3 py-2 rounded-lg border border-slate-300 text-xs font-mono focus:outline-none focus:ring-2 focus:ring-[#C9962E]/40 resize-y"
                    />
                  )}
                  {jsonErrors[field.key] && (
                    <p className="mt-1 text-[11px] text-red-600">{jsonErrors[field.key]}</p>
                  )}
                </>
              ) : field.type === 'url' && field.mediaPicker ? (
                <div>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={form[field.key]}
                      onChange={(e) => set(field.key, e.target.value)}
                      data-testid={`field-${field.key}`}
                      placeholder="Paste a URL or click Browse"
                      className="flex-1 px-3 py-2 rounded-lg border border-slate-300 text-sm focus:outline-none focus:ring-2 focus:ring-[#C9962E]/40"
                    />
                    <button
                      type="button"
                      onClick={() => setMediaPickerField(field.key)}
                      className="flex items-center gap-1.5 px-3 py-2 rounded-lg border border-slate-300 text-sm text-slate-600 hover:bg-slate-50 whitespace-nowrap"
                    >
                      <FolderOpen className="w-4 h-4" /> Browse
                    </button>
                  </div>
                  <ImagePreview src={form[field.key]} />
                </div>
              ) : field.key === 'icon' ? (
                <IconPickerInput
                  value={form[field.key]}
                  onChange={(val) => set(field.key, val)}
                  className="flex-1 px-3 py-2 rounded-lg border border-slate-300 text-sm focus:outline-none focus:ring-2 focus:ring-[#C9962E]/40"
                />
              ) : (
                <input
                  type={field.type === 'number' ? 'number' : 'text'}
                  value={form[field.key]}
                  onChange={(e) => set(field.key, field.type === 'number' ? Number(e.target.value) : e.target.value)}
                  data-testid={`field-${field.key}`}
                  className="w-full px-3 py-2 rounded-lg border border-slate-300 text-sm focus:outline-none focus:ring-2 focus:ring-[#C9962E]/40"
                />
              )}
              {field.type === 'url' && !field.mediaPicker && (
                <ImagePreview src={form[field.key]} />
              )}
            </div>
          ))}
        </div>
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-slate-100">
          <button onClick={onClose} className="px-4 py-2 rounded-lg border border-slate-300 text-sm text-slate-600 hover:bg-slate-50">Cancel</button>
          <button
            onClick={handleSave}
            disabled={saving}
            data-testid="save-item-btn"
            className="flex items-center gap-2 px-5 py-2 rounded-lg bg-[#1F2D45] text-white text-sm font-semibold hover:bg-[#2a3d5e] disabled:opacity-60"
          >
            {saving && <Loader2 className="w-4 h-4 animate-spin" />}
            <Save className="w-4 h-4" /> {initial ? 'Update' : 'Add'}
          </button>
        </div>
      </div>
      {mediaPickerField && (
        <MediaPickerModal
          onSelect={(url) => {
            set(mediaPickerField, url);
            setMediaPickerField(null);
          }}
          onClose={() => setMediaPickerField(null)}
        />
      )}
    </div>
  );
}

function ImageCell({ src }) {
  const [broken, setBroken] = useState(false);
  const placeholder = (
    <span className="inline-flex items-center justify-center h-10 w-14 rounded border border-dashed border-slate-300 bg-slate-50 text-slate-400">
      <ImageIcon className="w-4 h-4" />
    </span>
  );
  if (!src || broken) return placeholder;
  return (
    <img
      src={src}
      alt=""
      className="h-10 w-14 rounded object-cover border border-slate-200"
      onError={() => setBroken(true)}
    />
  );
}

export default function CollectionEditor() {
  const { name } = useParams();
  const qc = useQueryClient();
  const label = COLLECTION_LABELS[name] || name;
  const schema = COLLECTION_SCHEMAS[name] || [];

  const [modal, setModal] = useState(null); // null | { mode: 'add'|'edit', item? }
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(null);

  const endpointBase = name === 'pages' ? `/admin/pages` : `/admin/collections/${name}`;

  const { data: items = [], isLoading } = useQuery({
    queryKey: ['admin-col', name],
    queryFn: () => api.get(endpointBase).then((r) => r.data.data),
  });

  const handleSave = async (form) => {
    setSaving(true);
    try {
      if (modal.mode === 'edit') {
        await api.put(`${endpointBase}/${modal.item.id}`, form);
        toast.success('Item updated');
      } else {
        await api.post(endpointBase, form);
        toast.success('Item added');
      }
      qc.invalidateQueries({ queryKey: ['admin-col', name] });
      setModal(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this item?')) return;
    setDeleting(id);
    try {
      await api.delete(`${endpointBase}/${id}`);
      qc.invalidateQueries({ queryKey: ['admin-col', name] });
      toast.success('Item deleted');
    } catch {
      toast.error('Could not delete item');
    } finally {
      setDeleting(null);
    }
  };

  const handleToggle = async (item) => {
    try {
      await api.put(`${endpointBase}/${item.id}`, { visible: !item.visible });
      qc.invalidateQueries({ queryKey: ['admin-col', name] });
    } catch { toast.error('Could not update visibility'); }
  };

  // Display keys: first 3 non-system fields, but surface any mediaPicker field if it's not already in the first 3
  const displayKeys = (() => {
    const sys = ['visible', 'order'];
    const nonSys = schema.filter((f) => !sys.includes(f.key));
    const first3 = nonSys.slice(0, 3);
    const imageField = nonSys.find((f) => f.mediaPicker);
    if (imageField && !first3.find((f) => f.key === imageField.key)) {
      first3[first3.length - 1] = imageField;
    }
    return first3.map((f) => f.key);
  })();

  return (
    <div className="space-y-6" data-testid={`collection-editor-${name}`}>
      <div className="flex items-center justify-between">
        <div>
          <Link to="/admin/collections" className="flex items-center gap-1 text-sm text-slate-400 hover:text-slate-600 mb-2">
            <ChevronLeft className="w-4 h-4" /> Collections
          </Link>
          <h1 className="text-2xl font-bold text-[#1F2D45]">{label}</h1>
          <p className="text-slate-500 text-sm mt-1">{items.length} item{items.length !== 1 ? 's' : ''}</p>
        </div>
        <button
          onClick={() => setModal({ mode: 'add' })}
          data-testid="add-item-btn"
          className="flex items-center gap-2 px-4 py-2.5 rounded-lg bg-[#1F2D45] text-white text-sm font-semibold hover:bg-[#2a3d5e] transition-colors"
        >
          <Plus className="w-4 h-4" /> Add Item
        </button>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
        {isLoading ? (
          <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
        ) : items.length === 0 ? (
          <div className="text-center py-16 text-slate-400 text-sm">No items yet. Add your first one.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="text-left px-5 py-3.5 text-xs font-semibold text-slate-500 uppercase tracking-wide">No.</th>
                  {displayKeys.map((k) => (
                    <th key={k} className="text-left px-5 py-3.5 text-xs font-semibold text-slate-500 uppercase tracking-wide">{schema.find((f) => f.key === k)?.label ?? k}</th>
                  ))}
                  <th className="text-left px-5 py-3.5 text-xs font-semibold text-slate-500 uppercase tracking-wide">{schema.find((f) => f.key === 'visible')?.label ?? 'Visible'}</th>
                  <th className="px-5 py-3.5"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {items.map((item, idx) => (
                  <tr key={item.id} className="hover:bg-slate-50/50" data-testid={`item-row-${item.id}`}>
                    <td className="px-5 py-4 text-slate-400 text-xs">{idx + 1}</td>
                    {displayKeys.map((k) => {
                      const fieldSchema = schema.find((f) => f.key === k);
                      const isImg = fieldSchema?.mediaPicker || k.includes('img') || k.includes('image') || k.includes('photo') || k.includes('avatar') || k.includes('logo');
                      const val = item[k];
                      const isJson = val !== null && typeof val === 'object';
                      let cellContent;
                      if (isImg) {
                        cellContent = <ImageCell src={val} />;
                      } else if (isJson) {
                        const count = Array.isArray(val) ? val.length : Object.keys(val).length;
                        cellContent = (
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-500">
                            {count} {count === 1 ? 'item' : 'items'}
                          </span>
                        );
                      } else {
                        cellContent = String(val ?? '—');
                      }
                      return (
                        <td key={k} className="px-5 py-4 text-slate-700 max-w-xs truncate">
                          {cellContent}
                        </td>
                      );
                    })}
                    <td className="px-5 py-4">
                      <button onClick={() => handleToggle(item)} data-testid={`toggle-item-${item.id}`} className="p-1 rounded transition-colors">
                        {item.visible
                          ? <Eye className="w-4 h-4 text-emerald-500" />
                          : <EyeOff className="w-4 h-4 text-slate-400" />}
                      </button>
                    </td>
                    <td className="px-5 py-4">
                      <div className="flex items-center gap-1.5 justify-end">
                        <button onClick={() => setModal({ mode: 'edit', item })} data-testid={`edit-item-${item.id}`} className="p-1.5 rounded-lg text-slate-400 hover:text-[#1F2D45] hover:bg-slate-100 transition-colors">
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button onClick={() => handleDelete(item.id)} disabled={deleting === item.id} data-testid={`delete-item-${item.id}`} className="p-1.5 rounded-lg text-slate-400 hover:text-red-500 hover:bg-red-50 transition-colors disabled:opacity-50">
                          {deleting === item.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {modal && (
        <ItemForm
          schema={schema}
          initial={modal.item}
          onSave={handleSave}
          onClose={() => setModal(null)}
          saving={saving}
        />
      )}
    </div>
  );
}
